/**
 * useAutoMode — le mode autonome du live de groupe, branché : les générations, le choix, le tuteur
 * prévenu. Les règles sont dans `autoMode.ts` (réducteur pur).
 *
 * Les générations partent ensemble, du service directement, comme l'ancien (`Promise.allSettled` :
 * un même service y part plusieurs fois, la tranche Redux n'en garderait qu'une réponse) ; chaque
 * proposition arrive dès qu'elle est prête. Le choix passe par `apiCall` : la tranche
 * `activityTodoAsStudent` range l'activité créée dans la liste, comme aujourd'hui. Puis le tuteur,
 * seul, reçoit `autoModeActivityStarted` (`{ evaluation, activityId }`, `replay` pour la même), et
 * l'écran ouvre l'activité. Chaque transition laisse un fil d'Ariane ; chaque échec part dans Bugsnag.
 * Aux trois moments où l'ancien jouait un son (`AutoModeCue`), l'écran en est prévenu (`onCue`).
 */
import { useCallback, useReducer, useRef } from "react"
import { useDispatch } from "react-redux"

import { activitiesService } from "../../../data/services/activitiesService"
import { apiCall, type ApiService } from "../../shared/apiCall"
import { type EngineTelemetry } from "../../shared/telemetry"
import { type SendPolicy, type SendResult } from "../room/roomSender"
import {
    autoModeReducer,
    autoModeRequests,
    autoModeWantsQuiz,
    initialAutoModeState,
    type AutoModeCue,
    type AutoModeEvaluation,
    type AutoModeMaterial,
    type AutoModeSource,
    type AutoModeState,
} from "./autoMode"

type Send = (
    type: string,
    data: Record<string, unknown>,
    to: number[] | null,
    policy: SendPolicy
) => Promise<SendResult>

export type UseAutoModeOptions = {
    /** Les tuteurs de la séance : eux seuls apprennent le choix de l'élève. */
    tutorIds: number[]
    /** La salle a le quiz de prérequis (`prerequisite-quiz`). */
    roomHasQuiz: boolean
    send: Send
    telemetry: EngineTelemetry
    /** L'activité créée : l'écran l'ouvre. */
    onStarted: (activityId: number) => void
    /** Les moments où l'ancien mode autonome jouait un son : l'écran le joue. */
    onCue: (cue: AutoModeCue) => void
}

export type AutoModeController = {
    state: AutoModeState
    open: (source: AutoModeSource) => void
    evaluate: (evaluation: AutoModeEvaluation) => void
    choose: (material: AutoModeMaterial) => void
    replay: () => void
    close: () => void
}

type ServiceFn = (routeParams: Record<string, unknown>, body: Record<string, unknown>) => Promise<unknown>

const services = activitiesService as unknown as Record<string, ServiceFn>

const newBatchId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

/** La ressource d'un support généré : `{ data: { data } }` (une JsonResource), ou `{ data }`. */
function materialOf(response: unknown): AutoModeMaterial | null {
    const data = (response as { data?: { data?: AutoModeMaterial } & AutoModeMaterial } | null)?.data
    const material = (data?.data ?? data ?? null) as AutoModeMaterial | null
    return material?.id != null ? material : null
}

export function useAutoMode(options: UseAutoModeOptions): AutoModeController {
    const dispatch = useDispatch()
    const [state, apply] = useReducer(autoModeReducer, initialAutoModeState)
    const stateRef = useRef(state)
    stateRef.current = state
    const optionsRef = useRef(options)
    optionsRef.current = options
    /** Une génération d'un mode refermé ne pose plus rien. */
    const runRef = useRef(0)

    const step = useCallback((event: Parameters<typeof autoModeReducer>[1]) => {
        const before = stateRef.current.phase
        const after = autoModeReducer(stateRef.current, event).phase
        if (before !== after) optionsRef.current.telemetry.transition(before, after, event.type)
        apply(event)
    }, [])

    const open = useCallback(
        (source: AutoModeSource) => {
            const wasClosed = stateRef.current.phase === "closed"
            step({ type: "OPEN", source })
            if (wasClosed) optionsRef.current.onCue("auto_mode_opened")
        },
        [step]
    )

    const close = useCallback(() => {
        runRef.current += 1
        step({ type: "CLOSE" })
    }, [step])

    const evaluate = useCallback(
        (evaluation: AutoModeEvaluation) => {
            const current = stateRef.current
            if (current.phase !== "evaluating") return
            const requests = autoModeRequests(evaluation, current.source, newBatchId())
            step({ type: "EVALUATED", evaluation, requests: requests.length })
            if (requests.length === 0) return
            const run = ++runRef.current
            void Promise.allSettled(
                requests.map((request) =>
                    services[request.service](request.routeParams, request.body).then((response) => {
                        const material = materialOf(response)
                        if (material != null && runRef.current === run) {
                            apply({ type: "PROPOSAL", proposal: { intent: request.intent, material } })
                        }
                        return material
                    })
                )
            ).then((results) => {
                if (runRef.current !== run) return
                results.forEach((result) => {
                    if (result.status === "rejected") optionsRef.current.telemetry.error("generate", result.reason)
                })
                const count = results.filter((result) => result.status === "fulfilled" && result.value != null).length
                apply({ type: "GENERATED", count })
                // Les cartes arrivées, l'attente finie : le son de l'ancien, s'il y en a au moins une.
                if (count > 0) optionsRef.current.onCue("auto_mode_proposals")
            })
        },
        [step]
    )

    const start = useCallback(
        (key: string, body: Record<string, unknown>, evaluation: AutoModeEvaluation | "replay") => {
            if (stateRef.current.phase !== "choosing" || stateRef.current.starting != null) return
            apply({ type: "CHOSEN", key })
            apiCall<{ id?: number } | null>(
                dispatch as never,
                activitiesService.activityTodoAsStudent as unknown as ApiService,
                { data: body }
            )
                .then((activity) => {
                    if (activity?.id == null) throw new Error("auto mode: empty activity response")
                    const { tutorIds, send, onStarted, onCue } = optionsRef.current
                    if (tutorIds.length > 0) {
                        void send("autoModeActivityStarted", { evaluation, activityId: activity.id }, tutorIds, "keep")
                    }
                    step({ type: "CLOSE" })
                    onStarted(activity.id)
                    onCue("auto_mode_started")
                })
                .catch((cause: unknown) => {
                    optionsRef.current.telemetry.error("start", cause, { evaluation })
                    apply({ type: "START_FAILED" })
                })
        },
        [dispatch, step]
    )

    const choose = useCallback(
        (material: AutoModeMaterial) => {
            const current = stateRef.current
            if (current.phase !== "choosing") return
            start(
                String(material.id),
                {
                    material_id: material.id,
                    has_prerequisite_quiz: autoModeWantsQuiz(material, optionsRef.current.roomHasQuiz),
                },
                current.evaluation
            )
        },
        [start]
    )

    const replay = useCallback(() => {
        const current = stateRef.current
        const materialId = current.phase === "choosing" ? current.source.material?.id : null
        if (materialId == null) return
        start("replay", { material_id: materialId }, "replay")
    }, [start])

    return { state, open, evaluate, choose, replay, close }
}
